import { useTranslation } from 'react-i18next';
import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { CancellationPolicyView, CancellationScheduleRow, calendarDateKey } from '../cancellationPolicy';
import { formatMoney } from '../money';
import { CancellationServicesPanel } from './CancellationServicesPanel';

interface Props {
  /** La política ya resuelta: lo que se dibuja sale de ahí y de nada más. */
  view: CancellationPolicyView;
  /** Motivo por el que no hay política (aviso del backend), si lo hay. */
  notice: string | null;
}

/** Conserva hora y segundos de pared; no usa el huso del navegador. */
function formatBoundary(value: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})(?:T((?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?))?$/.exec(value);
  if (!match || !calendarDateKey(match[1])) return value;
  const [year, month, day] = match[1].split('-');
  return `${day}/${month}/${year}${match[2] ? ` · ${match[2]}` : ''}`;
}

/**
 * La política de cancelación del proveedor, dibujada en UN solo lugar: el
 * diálogo que cancela y el detalle del listado la leen igual, porque por
 * dónde entró el asesor no puede cambiar lo que la cancelación cuesta.
 *
 * Las dos formas del contrato conviven, cada una donde sirve: el costo de HOY
 * en grande (es el número con el que se decide) y el calendario del proveedor
 * debajo, con los cortes locales completos y sin inferir una fila vigente.
 * Sin ninguna de las dos queda el aviso honesto: no hay
 * política publicada, y cancelar igual puede costar.
 *
 * Los importes se muestran como llegaron: acá no se calcula nada.
 */
export function CancellationPolicyPanel({ view, notice }: Props) {
  const { t } = useTranslation();

  const money = (amountMinor?: number): string =>
    typeof amountMinor === 'number' && view.currency ? formatMoney({ amountMinor, currency: view.currency }) : '—';

  const ruleCost = (row: CancellationScheduleRow): string => {
    const percent = row.feePercent !== undefined ? `${row.feePercent}%` : undefined;
    const management =
      row.managementFeeMinor !== undefined && view.currency ? money(row.managementFeeMinor) : undefined;
    if (percent && management) {
      return t('travelServices.detail.policyRuleCost', { percent, fee: management });
    }
    if (percent) return percent;
    if (management) {
      return t('travelServices.detail.policyRuleManagement', { fee: management });
    }
    return '—';
  };

  const ruleWhen = (row: CancellationScheduleRow): string => {
    const from = row.from ? formatBoundary(row.from) : undefined;
    const to = row.to ? formatBoundary(row.to) : undefined;
    if (from && to) return t('travelServices.detail.policyRangeBetween', { from, to });
    if (to) return t('travelServices.detail.policyRangeUntil', { to });
    if (from) return t('travelServices.detail.policyRangeFrom', { from });
    return t('travelServices.detail.policyRangeAny');
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {view.status === 'loading' && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.detail.policyLoading')}
        </Typography>
      )}

      {/* El número con el que se decide, en grande. */}
      {view.cost && (
        <Alert severity={view.cost.costly ? 'error' : 'info'}>
          <Typography variant="subtitle1" component="p">
            {t(`travelServices.detail.cancelToday.${view.cost.key}`, {
              fee: money(view.cost.feeMinor),
              refund: money(view.cost.refundMinor),
            })}
          </Typography>
          {view.cost.refundMinor !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t('travelServices.detail.cancelRefundByProvider')}
            </Typography>
          )}
        </Alert>
      )}

      {view.fullVoucherValue && <Alert severity="error">{t('travelServices.detail.cancelFullVoucherValue')}</Alert>}

      {view.noShow && (
        <Box display="flex" flexDirection="column" gap={1}>
          {view.noShow.feeMinor !== undefined && (
            <Alert severity="warning">
              {t('travelServices.detail.noShow.cost', {
                fee: money(view.noShow.feeMinor),
              })}
            </Alert>
          )}
          {view.noShow.fullVoucherValue && (
            <Alert severity="warning">{t('travelServices.detail.noShow.fullVoucherValue')}</Alert>
          )}
        </Box>
      )}

      {/* El texto del proveedor, tal cual: es el que explica su política. */}
      {view.message && (
        <Typography variant="body2" color="text.secondary">
          {view.message}
        </Typography>
      )}

      {view.penaltyUnknown && <Alert severity="warning">{t('travelServices.detail.policyPenaltyUnknown')}</Alert>}
      {!!view.conditionalCharges?.length && (
        <Box>
          <Typography variant="subtitle2">{t('travelServices.detail.conditionalNotice')}</Typography>
          {view.conditionalCharges.map((rule, index) => (
            <Box key={index} sx={{ mt: 1 }}>
              <Typography variant="body2">{t(`travelServices.detail.conditionalEvent.${rule.event}`)}</Typography>
              {rule.charge.fullVoucherValue && (
                <Typography variant="body2">{t('travelServices.detail.conditionalFull')}</Typography>
              )}
              {rule.charge.feeMinor !== undefined && (
                <Typography variant="body2">{money(rule.charge.feeMinor)}</Typography>
              )}
              {rule.window && (
                <Typography variant="body2">
                  {t('travelServices.detail.conditionalWindow', { hours: rule.window.hoursBefore })}
                </Typography>
              )}
              {rule.message && <Typography variant="body2">{rule.message}</Typography>}
            </Box>
          ))}
        </Box>
      )}
      {view.refundUnknown && <Typography variant="body2">{t('travelServices.detail.policyRefundUnknown')}</Typography>}
      {view.servicePolicy && <CancellationServicesPanel policy={view.servicePolicy} currency={view.currency} />}
      {!view.servicePolicy && view.schedule.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('travelServices.detail.policyScheduleTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {t('travelServices.detail.policyScheduleLocalTime')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('travelServices.detail.policyScheduleWhen')}</TableCell>
                <TableCell>{t('travelServices.detail.policyScheduleCost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.schedule.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{ruleWhen(row)}</TableCell>
                  <TableCell>{ruleCost(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Sin política no se bloquea la cancelación: se dice que se cancela a
          ciegas, con el motivo del backend si lo hubo. */}
      {view.status === 'unpublished' && (
        <Alert severity="warning">{notice ?? t('travelServices.detail.cancelPolicyUnpublished')}</Alert>
      )}
    </Box>
  );
}
